import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { User, Subscription, Filters } from '../types'

interface AppStore {
  // Пользователь и авторизация
  user: User | null
  subscription: Subscription
  isAuthenticated: boolean
  authStatus: 'idle' | 'pending' | 'success' | 'error'
  
  // Навигация
  currentSection: string | null
  currentCategory: string | null
  breadcrumbs: Array<{ label: string; path: string }>
  
  // Поиск и фильтры
  searchQuery: string
  filters: Filters
  
  // Избранное
  favorites: string[]
  
  // UI состояние
  isLoading: boolean
  isSidebarOpen: boolean
  
  // Экшены
  setUser: (user: User | null) => void
  setSubscription: (subscription: Subscription) => void
  setAuthStatus: (status: 'idle' | 'pending' | 'success' | 'error') => void
  setCurrentSection: (section: string | null) => void
  setCurrentCategory: (category: string | null) => void
  setBreadcrumbs: (breadcrumbs: Array<{ label: string; path: string }>) => void
  setSearchQuery: (query: string) => void
  setFilters: (filters: Filters) => void
  clearFilters: () => void
  toggleFavorite: (contentId: string) => void
  setLoading: (loading: boolean) => void
  toggleSidebar: () => void
  
  // Computed
  hasActiveSubscription: () => boolean
  canAccessContent: (isPremium: boolean) => boolean
}

export const useAppStore = create<AppStore>()(
  persist(
    (set, get) => ({
      // Начальное состояние
      user: null,
      subscription: { isActive: false },
      isAuthenticated: false,
      authStatus: 'idle',
      currentSection: null,
      currentCategory: null,
      breadcrumbs: [],
      searchQuery: '',
      filters: {},
      favorites: [],
      isLoading: false,
      isSidebarOpen: false,

      // Экшены
      setUser: (user) => set({ user, isAuthenticated: !!user }),
      
      setSubscription: (subscription) => {
        set({ subscription })
      },
      
      setAuthStatus: (authStatus) => set({ authStatus }),
      
      setCurrentSection: (section) => set({ currentSection: section }),
      
      setCurrentCategory: (category) => set({ currentCategory: category }),
      
      setBreadcrumbs: (breadcrumbs) => set({ breadcrumbs }),
      
      setSearchQuery: (query) => set({ searchQuery: query }),
      
      setFilters: (filters) => set({ filters }),
      
      clearFilters: () => set({ filters: {}, searchQuery: '' }),
      
      toggleFavorite: (contentId) => {
        const { favorites } = get()
        const newFavorites = favorites.includes(contentId)
          ? favorites.filter(id => id !== contentId)
          : [...favorites, contentId]
        set({ favorites: newFavorites })
      },
      
      setLoading: (loading) => set({ isLoading: loading }),
      
      toggleSidebar: () => set((state) => ({ isSidebarOpen: !state.isSidebarOpen })),

      // Computed
      hasActiveSubscription: () => {
        const { subscription } = get()
        const isActive = subscription.isActive && 
               (!subscription.expiresAt || new Date(subscription.expiresAt) > new Date())
       
        return isActive
      },
      
      canAccessContent: (isPremium) => {
        if (!isPremium) return true
        return get().hasActiveSubscription()
      }
    }),
    {
      name: 'pauline-cake-storage',
      partialize: (state) => ({
        user: state.user,
        subscription: state.subscription,
        favorites: state.favorites,
        filters: state.filters
      })
    }
  )
) 